import { useCallback, useMemo, useState } from 'react';
import { usePrevious } from 'react-use';

import { Visit } from '@dreryk/egabinet-visit-frontend-api';

import { TranslatableText } from '@dreryk/react-common';
import { useHistoryPushWithBackAction } from '@dreryk/react-components';

import { visitPaths } from '~libs.desktop.shared.paths';
import { PatientDataAccessMode } from '~libs.shared.prescription';

import { visitTranslationKeys } from '../../../translations';
import { documentationScopeVisitor } from '../../../visit/VisitPanel.utils';

import {
   HistoryDownloadAccessRequestStep,
   HistoryDownloadStep,
   HistoryDownloadStepName,
} from './PrescriptionsHistoryDownload.types';

type UseHistoryDownloadStepReturn = [
   step: HistoryDownloadStep,
   methods: {
      goToAccessCode: (applicationId: string) => void;
      goToAccessRequest: (errorMessage?: TranslatableText) => void;
      goToIkpPlaceholder: VoidFunction;
      goToPrescriptionsHistory: (accessMode?: PatientDataAccessMode) => void;
      goToPreviousStep: VoidFunction;
      goToP1CommunicationFaultStep: (faultDescription?: string) => void;
   }
];

const INITIAL_STEP: HistoryDownloadAccessRequestStep = {
   name: HistoryDownloadStepName.ACCESS_REQUEST,
   state: {
      errorMessage: undefined,
   },
};

export const useHistoryDownloadStep = (): UseHistoryDownloadStepReturn => {
   const [step, setStep] = useState<HistoryDownloadStep>(INITIAL_STEP);
   const previousStep = usePrevious(step);

   return useMemo<UseHistoryDownloadStepReturn>(
      () => [
         step,
         {
            goToAccessCode: (applicationId: string) =>
               setStep({ name: HistoryDownloadStepName.ACCESS_CODE, state: { applicationId: applicationId } }),
            goToAccessRequest: (errorMessage?: TranslatableText) =>
               setStep({
                  name: HistoryDownloadStepName.ACCESS_REQUEST,
                  state: { errorMessage: typeof errorMessage === 'string' ? errorMessage : undefined },
               }),
            goToIkpPlaceholder: () => setStep({ name: HistoryDownloadStepName.NO_IKP_PLACEHOLDER }),
            goToPrescriptionsHistory: (accessMode) =>
               setStep({ name: HistoryDownloadStepName.PRESCRIPTIONS_HISTORY, state: { accessMode: accessMode } }),
            goToPreviousStep: () => setStep(previousStep ?? INITIAL_STEP),
            goToP1CommunicationFaultStep: (faultDescription: string | undefined) =>
               setStep({
                  name: HistoryDownloadStepName.P1_COMMUNICATION_FAULT,
                  state: { faultDescription: faultDescription },
               }),
         },
      ],
      [step, previousStep]
   );
};

type GoToPrescriptionsHistoryDownload = (
   visitId: number,
   documentationScope: Visit['documentationScope'],
   accessMode?: PatientDataAccessMode
) => void;

export const useGoToPrescriptionsHistoryDownload = (): GoToPrescriptionsHistoryDownload => {
   const push = useHistoryPushWithBackAction(false);
   const shouldNotNavigate = visitPaths.usePrescriptionsHistoryDownloadPathMatch();

   return useCallback(
      (visitId, documentationScope, accessMode) => {
         if (shouldNotNavigate) {
            return;
         }
         const getPath = documentationScopeVisitor(documentationScope)({
            full: () => visitPaths.getPrescriptionsHistoryDownloadPath,
            prescriptionOnly: () => visitPaths.getPrescriptionVisitPrescriptionsHistoryDownloadPath,
         });
         push(
            getPath({
               params: { visitId: visitId },
               searchParams: { accessMode: accessMode?.toLowerCase() },
            }),
            {
               label: visitTranslationKeys.goBackToVisit,
            }
         );
      },
      [push, shouldNotNavigate]
   );
};
